import React from 'react';
import { partnerLocationLabel } from 'components/TradeTabPanel/partnerGeometry';
import { ActivityStats } from 'types/interfaces/activity';
import { formatRelativeTime } from 'utils/relativeTime';
import { pulseTradesTile } from './pulseTrades';

// The four Live Pulse stat tiles (Asana 1218957945501621, Decisions 8 and 9).
// Full numbers with separators, never a `k`. Each tile gets a "yours" second
// line ONLY when its value is non-zero, so day one keeps the clean layout.

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

interface TileProps {
  value: number | undefined;
  label: string;
  tone?: 'purple' | 'green' | 'plain';
  icon?: 'send' | 'box';
  yours: string | null;
  isLoading: boolean;
  testId: string;
}

const Tile: React.FC<TileProps> = ({ value, label, tone = 'plain', icon, yours, isLoading, testId }) => (
  <div className={`home-pulse__tile home-pulse__tile--${tone}`} data-testid={testId}>
    <div className="home-pulse__value-row">
      {icon === 'send' && (
        <svg className="home-pulse__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d="M3 11.5 21 3l-6.5 18-3.5-7.5L3 11.5z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      )}
      {icon === 'box' && (
        <svg className="home-pulse__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d="M12 3 4 7v10l8 4 8-4V7l-8-4zm0 0v18M4 7l8 4 8-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      )}
      <span className="home-pulse__value" data-testid={`${testId}-value`}>
        {isLoading || value === undefined ? '—' : value.toLocaleString('en-US')}
      </span>
    </div>
    <span className="home-pulse__label">{label}</span>
    {yours && (
      <span className="home-pulse__yours" data-testid={`${testId}-yours`}>
        <span className="home-pulse__yours-dot" aria-hidden="true" />
        {yours}
      </span>
    )}
  </div>
);

interface Props {
  stats: ActivityStats | undefined;
  isLoading: boolean;
}

/** The four "yours" lines, each null when its value is zero (Decision 9). */
export const yoursLines = (stats: ActivityStats | undefined) => {
  const mine = stats?.mine ?? null;
  if (!mine) return { inTransit: null, delivered: null, last: null, cards: null };
  const inTransit =
    mine.inTransit > 0
      ? `${mine.inTransit} ${plural(mine.inTransit, 'is', 'are')} yours${mine.arrivingToday > 0 ? ` · ${mine.arrivingToday} ${plural(mine.arrivingToday, 'arrives', 'arrive')} today` : ''}`
      : null;
  const fromLabel = mine.deliveredTodayFromRegion ? partnerLocationLabel(mine.deliveredTodayFromRegion) : null;
  const delivered =
    mine.deliveredToday > 0 ? `${mine.deliveredToday} ${plural(mine.deliveredToday, 'was', 'were')} yours${fromLabel ? `, from ${fromLabel}` : ''}` : null;
  const last = mine.lastTradeAt ? `Your last: ${formatRelativeTime(mine.lastTradeAt)}` : null;
  const cards = mine.cardsTradedThisWeek > 0 ? `${mine.cardsTradedThisWeek} of them ${plural(mine.cardsTradedThisWeek, 'was', 'were')} yours` : null;
  return { inTransit, delivered, last, cards };
};

const LivePulseTiles: React.FC<Props> = ({ stats, isLoading }) => {
  const yours = yoursLines(stats);
  // Rolling 24h, all-time when the day is empty, never a 0 (pass 4)
  const trades = pulseTradesTile(stats);
  return (
    <>
      <Tile value={stats?.inTransitCount} label="in the mail right now" tone="purple" icon="send" yours={yours.inTransit} isLoading={isLoading} testId="pulse-transit" />
      <Tile value={stats?.packagesDeliveredToday} label="packages delivered today" tone="green" icon="box" yours={yours.delivered} isLoading={isLoading} testId="pulse-delivered" />
      <Tile value={trades.value} label={trades.label} yours={yours.last} isLoading={isLoading} testId="pulse-trades" />
      <Tile value={stats?.cardsTradedThisWeek} label="cards traded this week" yours={yours.cards} isLoading={isLoading} testId="pulse-week" />
    </>
  );
};

export default LivePulseTiles;
